import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { ViewLarge, Viewport, ViewSmall } from "bernie-view";

import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { AnalyticsConfig } from "uitk-react-utils";

import VisitorBlogCard from "./components/VisitorBlogCard";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { VisitorBlogsProps } from "./typings";
import { Article as VisitorBlog, TeArticleFlexModuleResult } from "typings/microserviceModels/te-article-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { withStores } from "stores";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { observer } from "mobx-react";

const VisitorBlogs = withStores(
  "flexModuleModelStore",
  "analytics",
  "thumbor"
)(
  observer((props: VisitorBlogsProps) => {
    const { flexTemplateNode, flexModuleModelStore, thumbor, context } = props;

    const { id, options } = flexTemplateNode;
    const { fmId, fmTitleId } = options;
    const model = flexModuleModelStore.getModel(id) as TeArticleFlexModuleResult | null;

    if (!model) {
      return null;
    }
    const { content, title } = model;

    if (!content?.items?.length) {
      return null;
    }

    const { items: visitorBlogs } = content;
    const locationName = context.searchContext.location?.name;
    const { formatText } = useLocalization();

    const localisedHeading = title || (locationName && formatText("visitorBlogs.title", locationName));

    const safelyResizeImage = (visitorBlog: VisitorBlog) => {
      const image = visitorBlog.media?.mediaUrl;

      return (
        image ?? thumbor.safelyGenerateThumborizedImageUrl(image, 600, 300, context, AspectRatioEnum.DEFAULT, true, 100)
      );
    };

    const analytics: AnalyticsConfig = {
      id: "TG.LP.Dest.Articles",
      callback: (moduleId, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.SCROLL,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
      },
    };

    const visitorBlogComponentArray = () => {
      const articleKeyHelper = new ItemKeyHelper("top-articles");

      return visitorBlogs.map((visitorBlog: VisitorBlog) => (
        <VisitorBlogCard
          key={articleKeyHelper.next()}
          visitorBlog={visitorBlog}
          visitorBlogImageUrl={safelyResizeImage(visitorBlog)}
        />
      ));
    };

    return (
      <Viewport>
        <ViewSmall>
          <div
            id={id}
            data-fm={fmId}
            data-fm-title-id={fmTitleId}
            className={`VisitorBlogs${visitorBlogs.length > 1 ? " VisitorBlogs-carousel" : ""}`}
          >
            <IsomorphicCarousel
              headline={localisedHeading}
              analytics={analytics}
              carouselName="Visitor Blogs"
              itemsVisible={{ lg: 1, md: 1, sm: 1 }}
              pageBy={1}
              peekWidth={24}
              buttonText={{
                nextButton: formatText("carousel.item.next"),
                prevButton: formatText("carousel.item.prev"),
              }}
            >
              {visitorBlogComponentArray()}
            </IsomorphicCarousel>
          </div>
        </ViewSmall>

        <ViewLarge>
          <div id={id} data-fm={fmId} data-fm-title-id={fmTitleId} className="VisitorBlogs uitk-cell all-cell-1-1">
            <UitkHeading level={3} size={600}>
              {localisedHeading}
            </UitkHeading>

            <UitkLayoutGrid className="all-t-margin-three" columns={3} space="three">
              {visitorBlogComponentArray()}
            </UitkLayoutGrid>
          </div>
        </ViewLarge>
      </Viewport>
    );
  })
);

export default VisitorBlogs;
